import { useId } from 'react';

import styles from './KaiLogo.module.css';

export type KaiLogoVariant = 'default' | 'reverse' | 'flat';

interface KaiLogoProps {
  /** Pixel size of the symbol. */
  size?: number;
  /** Optional wordmark text rendered alongside the symbol. */
  label?: string;
  /** Override label sub-text rendered under the wordmark. */
  sublabel?: string;
  /**
   * Mark variant per the brand spec:
   * - `default`: cream radial-gradient tile + Foundation Green glyph. Reads on
   *   light, colorful, and dark surfaces — the most universal choice.
   * - `reverse`: Foundation Green tile + cream radial-gradient glyph. Use on
   *   light surfaces where you want a dark filled mark.
   * - `flat`: glyph only, no tile, inherits `currentColor`. For tight headers.
   */
  variant?: KaiLogoVariant;
  className?: string;
}

/* ------------------------------------------------------------
   Brand-fixed values from kai_brand_v1_mini.pdf.
   The mark itself does NOT theme-shift — only the wordmark text.
   ------------------------------------------------------------ */
const TILE_GRADIENT_FROM = '#F7F7F2';
const TILE_GRADIENT_TO = '#CDEBEA';
const FOUNDATION_GREEN = '#002B2A';

const KAI_GLYPH_PATH =
  'M35.0304 62.76C40.254 56.4766 44.3124 49.178 46.786 41.4002C46.2818 40.3336 ' +
  '45.7985 39.2558 45.354 38.1641C44.2374 35.3891 43.3165 32.503 42.6415 29.5252' +
  'C41.3332 35.3697 38.9165 40.9836 35.7554 46.0308C31.2763 53.1433 25.079 ' +
  '59.4419 17.7207 63.3127L23.5901 73.0808C27.9679 70.1669 31.7721 66.6364 ' +
  '35.0332 62.7572L35.0304 62.76ZM59.5151 57.4613C60.6915 57.5558 61.8651 ' +
  '57.6794 63.0318 57.8405C65.9929 58.26 68.9554 58.9072 71.8693 59.8114C67.4596 ' +
  '55.7558 63.8068 50.8572 61.0193 45.5961C57.0985 38.1613 54.7429 29.6419 ' +
  '55.0707 21.335L43.6776 21.5336C44.0124 26.7822 45.1693 31.8419 46.8943 ' +
  '36.6044C49.7235 44.2683 54.0165 51.435 59.5138 57.4627L59.5151 57.4613ZM63.6165 ' +
  '59.9558C55.5638 58.5738 47.2124 58.7072 39.2415 60.4544C38.5693 61.4266 ' +
  '37.8763 62.3822 37.1568 63.3127C35.3082 65.6725 33.2651 67.9114 31.0221 ' +
  '69.9836C36.7401 68.1919 42.8151 67.4766 48.7707 67.6905C57.1707 68.0127 ' +
  '65.7249 70.2322 72.7554 74.6669L78.2818 64.7002C73.5693 62.3655 68.611 ' +
  '60.8391 63.6207 59.9502L63.6179 59.9572L63.6165 59.9558Z';

const cn = (...parts: (string | undefined | false)[]) =>
  parts.filter(Boolean).join(' ');

export function KaiLogo({
  size = 36,
  label,
  sublabel,
  variant = 'default',
  className,
}: KaiLogoProps) {
  const uid = useId();
  const tileGradientId = `kai-tile-${uid}`;
  const glyphGradientId = `kai-glyph-${uid}`;

  const isFlat = variant === 'flat';
  const isReverse = variant === 'reverse';

  return (
    <span className={cn(styles.logo, className)}>
      <span
        className={isFlat ? styles.flatMark : styles.mark}
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <radialGradient id={tileGradientId} cx="50%" cy="0%" r="100%">
              <stop offset="0%" stopColor={TILE_GRADIENT_FROM} />
              <stop offset="100%" stopColor={TILE_GRADIENT_TO} />
            </radialGradient>
            <radialGradient id={glyphGradientId} cx="50%" cy="20%" r="80%">
              <stop offset="0%" stopColor={TILE_GRADIENT_FROM} />
              <stop offset="100%" stopColor={TILE_GRADIENT_TO} />
            </radialGradient>
          </defs>

          {!isFlat && (
            <rect
              width="96"
              height="96"
              rx="22"
              fill={isReverse ? FOUNDATION_GREEN : `url(#${tileGradientId})`}
            />
          )}

          <path
            d={KAI_GLYPH_PATH}
            fill={
              isFlat
                ? 'currentColor'
                : isReverse
                  ? `url(#${glyphGradientId})`
                  : FOUNDATION_GREEN
            }
          />
        </svg>
      </span>

      {label && (
        <span className={styles.wordmarkGroup}>
          <span className={styles.wordmark}>{label}</span>
          {sublabel && <span className={styles.sublabel}>{sublabel}</span>}
        </span>
      )}
    </span>
  );
}
